import type {
  VenueReservation,
  VenueReservationAnswer,
} from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useNotification from "@/hooks/useNotification";
import { sendGaEvent } from "@/tools/analytics";
import { useMutation } from "@apollo/client";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { useCallback } from "react";
import {
  EDIT_VENUE_RESERVATION_ANSWER_MUTATION,
  EditVenueReservationMutationInput,
  EditVenueReservationMutationResponse,
} from "./mutations/EditVenueReservationAnswer";
import { buildReservationAnswerUpdate } from "./reservationAnswerCache";
import { translateReservationAnswer } from "./reservationAnswerUtils";
import type { ReservationsBucket } from "./reservationsTypes";

type UseReservationActionsOptions = {
  userId?: string;
  updateBucket: (
    key: string,
    updater: (bucket: ReservationsBucket) => ReservationsBucket,
  ) => ReservationsBucket;
  activeBucketKeyRef: RefObject<string>;
  setPendingReservations: Dispatch<SetStateAction<VenueReservation[]>>;
};

/**
 * Provides mutation helpers for accepting, rejecting, or resetting reservations.
 */
export default function useReservationActions({
  userId,
  updateBucket,
  activeBucketKeyRef,
  setPendingReservations,
}: UseReservationActionsOptions) {
  const { addNotification } = useNotification();
  const { t } = useLocalization();

  const [
    editVenueReservationAnswerMutation,
    editVenueReservationAnswerMutationResponse,
  ] = useMutation<
    EditVenueReservationMutationResponse,
    EditVenueReservationMutationInput
  >(EDIT_VENUE_RESERVATION_ANSWER_MUTATION);

  /**
   * Executes the answer mutation and applies notifications and cache updates.
   */
  const editVenueReservationAnswer = useCallback(
    (
      reservationId: string,
      answer: `${VenueReservationAnswer}` | null,
      options?: { venueId?: string; venueName?: string; venuePortalUserId?: string },
    ) =>
      editVenueReservationAnswerMutation({
        variables: {
          id: reservationId,
          answer,
          ...options,
        },
        onCompleted() {
          sendGaEvent(
            `${translateReservationAnswer([
              "accept",
              "reject",
              "reset",
            ] as const)(answer)}_reservation`,
            {
              reservation_id: reservationId,
            },
          );
          addNotification({
            severity: "success",
            message: t(
              `notification:reservation.${translateReservationAnswer([
                "accepted",
                "rejected",
                "reset",
              ] as const)(answer)}Reservation`,
            ),
          });
        },
        onError() {
          addNotification({
            severity: "error",
            message: t("notification:reservation.failedToUpdateReservation"),
          });
        },
        update: buildReservationAnswerUpdate({
          reservationId,
          answer,
          userId,
          options,
          updateBucket,
          activeBucketKeyRef,
          setPendingReservations,
        }),
      }),
    [
      editVenueReservationAnswerMutation,
      userId,
      addNotification,
      t,
      updateBucket,
      activeBucketKeyRef,
      setPendingReservations,
    ],
  );

  /**
   * Marks a reservation as accepted.
   */
  const acceptReservation = useCallback(
    async (reservationId: string, venueId?: string, venueName?: string) => {
      await editVenueReservationAnswer(reservationId, "ACCEPTED", {
        venueId,
        venueName,
      });
    },
    [editVenueReservationAnswer],
  );

  /**
   * Marks a reservation as not accepted.
   */
  const rejectReservation = useCallback(
    async (reservationId: string) => {
      await editVenueReservationAnswer(reservationId, "NOT_ACCEPTED");
    },
    [editVenueReservationAnswer],
  );

  /**
   * Resets a reservation answer.
   */
  const resetReservation = useCallback(
    async (reservationId: string) => {
      await editVenueReservationAnswer(reservationId, null);
    },
    [editVenueReservationAnswer],
  );

  return {
    editVenueReservationAnswerMutationResponse,
    editVenueReservationAnswer,
    acceptReservation,
    rejectReservation,
    resetReservation,
  };
}
